// FROM CDN
import Swiper from 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.mjs';
await themeComponent(theme.components['mc-element'], false);

class McSwiper extends McElement {
  options; swiper;
  paginationSelector = '.swiper-pagination';
  navigationPrev = '.swiper-button-prev';
  navigationNext = '.swiper-button-next';
  scrollbarSelector = '.swiper-scrollbar';

  _ro = null;          // ResizeObserver
  _inited = false;     // guard to avoid double init

  connectedCallback() {
    // Clean up any previous instance
    if (this.swiper?.destroy) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Observe size changes so Swiper re-measures after flex/justify changes
    this._ro = new ResizeObserver(() => {
      if (this.swiper) {
        this.swiper.updateSize();
        this.swiper.updateSlides();
        this.swiper.update();
      }
    });
    this._ro.observe(this);

    // If no <mc-swiper-breakpoint> children, init immediately (unless data-noinit="true")
    const breakpoints = this.querySelectorAll('mc-swiper-breakpoint');
    if (breakpoints.length === 0) {
      if (this.dataset.noinit === undefined || this.dataset.noinit === 'false') {
        this.init();
      }
    }
  }

  disconnectedCallback() {
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
    if (this.swiper?.destroy) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this._inited = false;
  }

  init = () => {
    // Avoid double init; allow re-init after destroy
    if (this._inited && this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this._inited = true;

    // Base options + robust observers
    this.options = {
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      roundLengths: true,
      centeredSlidesBounds: true,
    };

    // Read selectors (allow overrides via data-*)
    this.paginationSelector = this.dataset.paginationSelector || this.paginationSelector;
    this.navigationPrev = this.dataset.navigationPrev || this.navigationPrev;
    this.navigationNext = this.dataset.navigationNext || this.navigationNext;
    this.scrollbarSelector = this.dataset.scrollbarSelector || this.scrollbarSelector;

    // Apply feature toggles / values
    this.setNavigation(this.dataset.navigation !== undefined && this.dataset.navigation !== 'false');
    this.setPagination(this.dataset.pagination !== undefined && this.dataset.pagination !== 'false');
    this.setDynamicBullets(this.dataset.dynamicBullets !== undefined && this.dataset.dynamicBullets !== 'false');
    this.setPaginationType(this.dataset.paginationType);
    this.setPaginationCustom(this.dataset.paginationCustom);
    this.setScrollbar(this.dataset.scrollbar !== undefined && this.dataset.scrollbar !== 'false');
    this.setMousewheel(this.dataset.mousewheel !== undefined && this.dataset.mousewheel !== 'false');
    this.setScrollbarHide(this.dataset.scrollbarHide !== undefined && this.dataset.scrollbarHide !== 'false');
    this.setDirection(this.dataset.direction);

    // >>> spacing + offsets (order matters)
    this.setSpaceBetween(this.dataset.spaceBetween);
    this.setSlidesOffsetBefore(this.dataset.slidesOffsetBefore);
    this.setSlidesOffsetAfter(this.dataset.slidesOffsetAfter);
    this.setOffsetsFromPadding(); // mirrors element padding as offsets

    this.setSlides(this.dataset.slides);
    this.setCentered(this.dataset.centered !== undefined && this.dataset.centered !== 'false');
    this.setCenterInsufficient(this.dataset.centerInsufficient !== undefined && this.dataset.centerInsufficient !== 'false');

    this.setFree(this.dataset.free !== undefined && this.dataset.free !== 'false');
    this.setLoop(this.dataset.loop !== undefined && this.dataset.loop !== 'false');
    this.setAutoplay(this.dataset.autoplay);
    this.setAutoplayDisableInteraction(this.dataset.autoplayDisableInteraction !== undefined && this.dataset.autoplayDisableInteraction !== 'false');
    this.setWatchProgress(this.dataset.watchProgress !== undefined && this.dataset.watchProgress !== 'false');
    this.setThumbs(this.dataset.thumbs);
    this.setInitialSlide(this.dataset.initialSlide);
    this.setSlideGroups(this.dataset.slideGroups);
    this.setAutoplayProgress(this.dataset.autoplayProgress);
    this.setLoopAddBlankSlides(this.dataset.loopAddBlankSlides !== undefined && this.dataset.loopAddBlankSlides !== 'false');
    this.setCssMode(this.dataset.cssMode !== undefined && this.dataset.cssMode !== 'false');
    this.setLoopAdditionalSlides(this.dataset.loopAdditionalSlides);
    this.setSpeed(this.dataset.speed);
    this.setBreakpoints();

    if (this.dataset.log) console.log('McSwiper options:', this.options);

    // Finally, instantiate
    this.swiper = new Swiper(this, this.options);
  }

  // Allow programmatic replacement of options (keeps observers)
  setOptions = (options) => {
    if (this.swiper?.destroy) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.options = {
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      roundLengths: true,
      centeredSlidesBounds: true,
      ...(options || {}),
    };
    this.swiper = new Swiper(this, this.options);
    this._inited = true;
  }

  // Safe public update
  updateNow = () => {
    if (this.swiper) {
      this.swiper.updateSize();
      this.swiper.updateSlides();
      this.swiper.update();
    }
  }

  setInitialSlide = (initialSlide) => {
    const s = parseInt(initialSlide);
    if (!isNaN(s)) this.options.initialSlide = s;
  }

  setAutoplayProgress = (autoplayProgress) => {
    if (autoplayProgress) {
      const progressCircle = document.querySelector(autoplayProgress);
      if (progressCircle) {
        this.options.on = this.options.on || {};
        this.options.on.autoplayTimeLeft = (s, time, progress) => {
          progressCircle.style.setProperty('--progress', 1 - progress);
        };
      }
    }
  }

  setSpeed = (speed) => {
    const s = parseInt(speed);
    if (!isNaN(s)) this.options.speed = s;
  }

  setCssMode = (cssMode) => {
    if (cssMode) this.options.cssMode = true;
  }

  setSlideGroups = (slideGroups) => {
    const s = parseInt(slideGroups);
    if (!isNaN(s)) this.options.slidesPerGroup = s;
  }

  setNavigation = (navigation) => {
    if (navigation) {
      this.options.navigation = {
        nextEl: this.navigationNext,
        prevEl: this.navigationPrev
      };
    }
  }

  setPagination = (pagination) => {
    if (pagination) {
      const el = this.querySelector(this.paginationSelector); // scoped!
      if (el) {
        this.options.pagination = {
          ...(this.options.pagination || {}),
          el,
          clickable: true
        };
      } else {
        console.warn('No pagination element found inside <mc-swiper>');
      }
    }
  }

  setDynamicBullets = (dynamicBullets) => {
    if (dynamicBullets) {
      this.options.pagination = this.options.pagination || {};
      this.options.pagination.dynamicBullets = true;
    }
  }

  setPaginationType = (type) => {
    if (type !== undefined) {
      this.options.pagination = this.options.pagination || {};
      this.options.pagination.type = type;
    }
  }

  setPaginationCustom = (custom) => {
    if (custom !== undefined) {
      const parts = String(custom).split(',');
      this.options.pagination = this.options.pagination || {};
      this.options.pagination.renderBullet = (i, c) => `<span class="${c}">${parts[i] ?? ''}</span>`;
    }
  }

  setScrollbar = (scrollbar) => {
    if (scrollbar) {
      this.options.scrollbar = {
        el: this.scrollbarSelector,
        draggable: true
      };
    }
  }

  setScrollbarHide = (hide) => {
    if (hide) {
      this.options.scrollbar = this.options.scrollbar || {};
      this.options.scrollbar.hide = true;
    }
  }

  setDirection = (direction) => {
    if (direction !== undefined) this.options.direction = direction;
  }

  setSpaceBetween = (spaceBetween) => {
    const s = parseInt(spaceBetween);
    if (!isNaN(s)) this.options.spaceBetween = s;
  }

  // NEW: explicit offsets via data-*, e.g. data-slides-offset-before="16"
  setSlidesOffsetBefore = (v) => {
    const n = parseFloat(v);
    if (Number.isFinite(n)) this.options.slidesOffsetBefore = n;
  }
  setSlidesOffsetAfter = (v) => {
    const n = parseFloat(v);
    if (Number.isFinite(n)) this.options.slidesOffsetAfter = n;
  }
  // NEW: mirror this element's padding as offsets so first/last slide align with section gutters
  setOffsetsFromPadding = () => {
    const cs = getComputedStyle(this);
    const left = parseFloat(cs.paddingLeft) || 0;
    const right = parseFloat(cs.paddingRight) || 0;
    if (left && this.options.slidesOffsetBefore == null) this.options.slidesOffsetBefore = left;
    if (right && this.options.slidesOffsetAfter == null) this.options.slidesOffsetAfter = right;
  }
  // NEW: center even when not enough slides to fill a row
  setCenterInsufficient = (enabled) => {
    if (enabled) this.options.centerInsufficientSlides = true;
  }

  setSlides = (slides) => {
    if (slides !== undefined) {
      const v = (String(slides).trim().toLowerCase() === 'auto')
        ? 'auto'
        : parseFloat(slides);
      this.options.slidesPerView = Number.isFinite(v) ? v : 'auto';
    }
  }

  setCentered = (centered) => {
    if (centered) this.options.centeredSlides = true;
  }

  setMousewheel = (mousewheel) => {
    if (mousewheel) this.options.mousewheel = true;
  }

  setFree = (free) => {
    if (free) this.options.freeMode = true;
  }

  setLoop = (loop) => {
    if (loop) this.options.loop = true;
  }

  setAutoplay = (autoplay) => {
    const s = parseInt(autoplay);
    if (!isNaN(s)) {
      this.options.autoplay = {
        ...(this.options.autoplay || {}),
        delay: s
      };
    }
  }

  setAutoplayDisableInteraction = (autoplayDisableInteraction) => {
    if (autoplayDisableInteraction) {
      this.options.autoplay = this.options.autoplay || {};
      this.options.autoplay.disableOnInteraction = true;
    }
  }

  setLoopAddBlankSlides = (loopAddBlankSlides) => {
    if (this.options.loop) this.options.loopAddBlankSlides = !!loopAddBlankSlides;
  }

  setWatchProgress = (watchProgress) => {
    if (watchProgress) this.options.watchSlidesProgress = true;
  }

  setLoopAdditionalSlides = (loopAdditionalSlides) => {
    const s = parseInt(loopAdditionalSlides);
    if (!isNaN(s)) this.options.loopAdditionalSlides = s;
  }

  setThumbs = (selector) => {
    if (!selector) return;
    const el = document.querySelector(selector);
    if (!el) return;

    // If the target is another <mc-swiper>, use its Swiper instance
    const maybeMc = el.tagName?.toLowerCase() === 'mc-swiper' ? el : el.closest('mc-swiper');
    const thumbsSwiper = maybeMc?.swiper || el.swiper;

    if (thumbsSwiper) {
      this.options.thumbs = { swiper: thumbsSwiper };
    } else {
      // Fallback: if element is a native .swiper container, Swiper can take it directly
      this.options.thumbs = { swiper: el };
    }
  }

  setBreakpoints = () => {
    const bps = this.querySelectorAll('mc-swiper-breakpoint');
    if (bps.length > 0) {
      this.options.breakpoints = {};
      bps.forEach((bp) => {
        if (typeof bp.getPointOptions === 'function') {
          const w = parseInt(bp.width ?? bp.dataset?.breakpoint, 10);
          if (Number.isFinite(w)) this.options.breakpoints[w] = bp.getPointOptions();
        }
      });
    }
  }

  slideTo = (index, speed) => {
    if (this.swiper) this.swiper.slideTo(index, speed);
  }
}
window.McSwiper = McSwiper;
export default McSwiper;

class McSwiperBreakpoint extends McElement {
  options = {}; width; swiper; isConnected = false;

  init = () => {
    this.options = {};
    this.width = parseInt(this.dataset.breakpoint, 10);

    this.setSlides(this.dataset.slides);
    this.setSpaceBetween(this.dataset.spaceBetween);
    this.setSlideGroups(this.dataset.slideGroups);

    this.swiper = this.closest('mc-swiper');

    // If all breakpoints are connected, trigger parent init once
    const bps = Array.from(this.swiper.querySelectorAll('mc-swiper-breakpoint'));
    const allConnected = bps.every((bp) => bp.isConnected);
    if (allConnected) this.swiper.init();
  }

  connectedCallback() {
    this.isConnected = true;
    this.init();
  }

  getPointOptions = () => {
    return this.options;
  }

  setSpaceBetween = (spaceBetween) => {
    const s = parseInt(spaceBetween, 10);
    if (!isNaN(s)) this.options.spaceBetween = s;
  }

  setSlideGroups = (slideGroups) => {
    const s = parseInt(slideGroups, 10);
    if (!isNaN(s)) this.options.slidesPerGroup = s;
  }

  setSlides = (slides) => {
    if (slides !== undefined) {
      const v = (String(slides).trim().toLowerCase() === 'auto')
        ? 'auto'
        : parseFloat(slides);
      this.options.slidesPerView = Number.isFinite(v) ? v : 'auto';
    }
  }
}
window.McSwiperBreakpoint = McSwiperBreakpoint;

if (!window.customElements.get('mc-swiper')) {
  window.customElements.define('mc-swiper', McSwiper);
  McUtils.addCss('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css');
}
if (!window.customElements.get('mc-swiper-breakpoint')) {
  window.customElements.define('mc-swiper-breakpoint', McSwiperBreakpoint);
}
